/**
 * Spawner - Package Generation System
 * 
 * Handles spawning packages using PackageSizeGenerator for dimensions.
 * Creates Rapier physics bodies with proper sizing and mass.
 */

import { PhysicsWorld, PhysicsBody, BodyType } from '../engine/PhysicsWorld';
import { PackageSizeGenerator } from './PackageSizeGenerator';
import { SPAWN_RATES, PACKAGE_PHYSICS, CONVEYOR_PHYSICS } from './Constants';

/** Container bounds for calculating scale factor */
export interface ContainerBounds {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

/**
 * Spawner class - creates packages with proper sizing
 */
export class Spawner {
    private physics: PhysicsWorld;
    private sizeGenerator: PackageSizeGenerator;

    constructor(physics: PhysicsWorld) {
        this.physics = physics;
        this.sizeGenerator = new PackageSizeGenerator();
    }

    /**
     * Set the container bounds for scale factor calculation.
     */
    setContainerBounds(bounds: ContainerBounds): void {
        const containerWidth = bounds.right - bounds.left;
        this.sizeGenerator.setScaleFactor(containerWidth);
    }

    /**
     * Get the current scale factor.
     */
    getScaleFactor(): number {
        return this.sizeGenerator.getScaleFactor();
    }

    /**
     * Get the size generator for direct access to dimension generation.
     */
    getSizeGenerator(): PackageSizeGenerator {
        return this.sizeGenerator;
    }

    /**
     * Spawn a random package at the given position.
     * Uses configured spawn rates (85% box, 10% bag, 5% triangle).
     */
    spawnRandom(x: number, y: number): PhysicsBody {
        const roll = Math.random();

        if (roll < SPAWN_RATES.box) {
            return this.spawnBox(x, y);
        } else if (roll < SPAWN_RATES.box + SPAWN_RATES.bag) {
            return this.spawnBag(x, y);
        } else {
            return this.spawnTriangle(x, y);
        }
    }

    /**
     * Spawn a box with generated dimensions.
     */
    spawnBox(x: number, y: number, angle?: number): PhysicsBody {
        const dims = this.sizeGenerator.generateBox();

        return this.physics.createBody({
            type: BodyType.Dynamic,
            x,
            y,
            angle: angle ?? Math.random() * Math.PI * 2,
            shape: { type: 'box', width: dims.width, height: dims.height },
            friction: PACKAGE_PHYSICS.box.friction,
            density: dims.density,
            restitution: PACKAGE_PHYSICS.box.restitution,
            linearDamping: PACKAGE_PHYSICS.box.linearDamping,
            angularDamping: CONVEYOR_PHYSICS.defaultAngularDamping,
            ccdEnabled: true,
        });
    }

    /**
     * Spawn a bag with generated dimensions.
     */
    spawnBag(x: number, y: number, angle?: number): PhysicsBody {
        const dims = this.sizeGenerator.generateBag();

        return this.physics.createBody({
            type: BodyType.Dynamic,
            x,
            y,
            angle: angle ?? Math.random() * Math.PI * 2,
            shape: { 
                type: 'roundedBox', 
                width: dims.width, 
                height: dims.height, 
                radius: dims.chamferRadius 
            },
            friction: PACKAGE_PHYSICS.bag.friction,
            density: dims.density,
            restitution: PACKAGE_PHYSICS.bag.restitution,
            linearDamping: PACKAGE_PHYSICS.bag.linearDamping,
            angularDamping: CONVEYOR_PHYSICS.defaultAngularDamping,
            ccdEnabled: true,
        });
    }

    /**
     * Spawn a triangle with generated dimensions.
     */
    spawnTriangle(x: number, y: number, angle?: number): PhysicsBody {
        const dims = this.sizeGenerator.generateTriangle();

        return this.physics.createBody({
            type: BodyType.Dynamic,
            x,
            y,
            angle: angle ?? dims.rotation,
            shape: { type: 'triangle', radius: dims.radius },
            friction: PACKAGE_PHYSICS.triangle.friction,
            density: dims.density,
            restitution: PACKAGE_PHYSICS.triangle.restitution,
            linearDamping: PACKAGE_PHYSICS.triangle.linearDamping,
            angularDamping: CONVEYOR_PHYSICS.defaultAngularDamping,
            ccdEnabled: true,
        });
    }
}
